import type { Currency, CurrencyIsoCode } from './types';

/**
 * Currency reference set, ISO 4217. Covers the major business markets the
 * suite currently targets plus the largest world currencies — enough for
 * most "base currency" dropdowns. Add more as needed; order is by frequency
 * of expected use (EUR/GBP/USD first, then alphabetical-by-name).
 */
export const CURRENCIES: readonly Currency[] = [
  { isoCode: 'EUR', name: 'Euro', symbol: '€', decimalDigits: 2 },
  { isoCode: 'GBP', name: 'Pound Sterling', symbol: '£', decimalDigits: 2 },
  { isoCode: 'USD', name: 'US Dollar', symbol: '$', decimalDigits: 2 },

  { isoCode: 'AED', name: 'UAE Dirham', symbol: 'د.إ', decimalDigits: 2 },
  { isoCode: 'ARS', name: 'Argentine Peso', symbol: '$', decimalDigits: 2 },
  { isoCode: 'AUD', name: 'Australian Dollar', symbol: 'A$', decimalDigits: 2 },
  { isoCode: 'BRL', name: 'Brazilian Real', symbol: 'R$', decimalDigits: 2 },
  { isoCode: 'CAD', name: 'Canadian Dollar', symbol: 'C$', decimalDigits: 2 },
  { isoCode: 'CHF', name: 'Swiss Franc', symbol: 'CHF', decimalDigits: 2 },
  { isoCode: 'CLP', name: 'Chilean Peso', symbol: '$', decimalDigits: 0 },
  { isoCode: 'CNY', name: 'Chinese Yuan', symbol: '¥', decimalDigits: 2 },
  { isoCode: 'COP', name: 'Colombian Peso', symbol: '$', decimalDigits: 2 },
  { isoCode: 'CZK', name: 'Czech Koruna', symbol: 'Kč', decimalDigits: 2 },
  { isoCode: 'DKK', name: 'Danish Krone', symbol: 'kr', decimalDigits: 2 },
  { isoCode: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', decimalDigits: 2 },
  { isoCode: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', decimalDigits: 2 },
  { isoCode: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', decimalDigits: 2 },
  { isoCode: 'ILS', name: 'Israeli New Shekel', symbol: '₪', decimalDigits: 2 },
  { isoCode: 'INR', name: 'Indian Rupee', symbol: '₹', decimalDigits: 2 },
  { isoCode: 'ISK', name: 'Icelandic Krona', symbol: 'kr', decimalDigits: 0 },
  { isoCode: 'JPY', name: 'Japanese Yen', symbol: '¥', decimalDigits: 0 },
  { isoCode: 'KRW', name: 'South Korean Won', symbol: '₩', decimalDigits: 0 },
  { isoCode: 'MXN', name: 'Mexican Peso', symbol: '$', decimalDigits: 2 },
  { isoCode: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', decimalDigits: 2 },
  { isoCode: 'NOK', name: 'Norwegian Krone', symbol: 'kr', decimalDigits: 2 },
  { isoCode: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', decimalDigits: 2 },
  { isoCode: 'PHP', name: 'Philippine Peso', symbol: '₱', decimalDigits: 2 },
  { isoCode: 'PLN', name: 'Polish Zloty', symbol: 'zł', decimalDigits: 2 },
  { isoCode: 'RON', name: 'Romanian Leu', symbol: 'lei', decimalDigits: 2 },
  { isoCode: 'SAR', name: 'Saudi Riyal', symbol: 'ر.س', decimalDigits: 2 },
  { isoCode: 'SEK', name: 'Swedish Krona', symbol: 'kr', decimalDigits: 2 },
  { isoCode: 'SGD', name: 'Singapore Dollar', symbol: 'S$', decimalDigits: 2 },
  { isoCode: 'THB', name: 'Thai Baht', symbol: '฿', decimalDigits: 2 },
  { isoCode: 'TRY', name: 'Turkish Lira', symbol: '₺', decimalDigits: 2 },
  { isoCode: 'VND', name: 'Vietnamese Dong', symbol: '₫', decimalDigits: 0 },
  { isoCode: 'ZAR', name: 'South African Rand', symbol: 'R', decimalDigits: 2 },
];

const CURRENCIES_BY_CODE: Map<CurrencyIsoCode, Currency> = new Map(
  CURRENCIES.map((c) => [c.isoCode, c]),
);

/** Look up a currency by ISO code. Returns `undefined` for unknown codes. */
export function findCurrency(isoCode: CurrencyIsoCode): Currency | undefined {
  return CURRENCIES_BY_CODE.get(isoCode);
}
